import { useCallback } from 'react';
import { useRelayEnvironment } from 'react-relay';
import { commitLocalUpdate } from 'relay-runtime';
import type { AnalyticsEventPayload } from '@atlaskit/analytics-next';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import type { Issue } from '@atlassian/jira-assign-issue-parent-modal/src/model/types.tsx';
import type { IssueId } from '@atlassian/jira-shared-types/src/general.tsx';
import type { IssueParent } from '@atlassian/jira-issue-parent-services/src/services/types.tsx';
import { JiraIssueAri } from '@atlassian/ari/jira';
import {
	useAnalyticsEvents,
	type UIAnalyticsEvent,
} from '@atlassian/jira-product-analytics-bridge';
import { PARENT_TYPE } from '@atlassian/jira-platform-field-config';
import { FIELD_TYPE_MAP } from '@atlassian/jira-issue-analytics/src/services/update-issue-field/constants.tsx';
import { getUpdateAnalyticsFlowHelper } from '@atlassian/jira-issue-analytics/src/services/update-issue-field/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useFlagsService } from '@atlassian/jira-flags/src/services/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useGetReparentUpdater } from '../../services/reparent-mutation/utils.tsx';
import { PACKAGE_NAME, TEAM_NAME } from '../../common/constants.tsx';
import { UPDATE_ITEM_PARENT_FAIL_GENERIC_FLAG } from '../../services/reparent-mutation/constants.tsx';

export const useReparentingFromModal = ({ projectKey }: { projectKey?: string }) => {
	const cloudId = useCloudId();
	const { showFlag } = useFlagsService();

	const environment = useRelayEnvironment();
	const updateParentConnections = useGetReparentUpdater();
	let createAnalyticsEvent: ((_payload: AnalyticsEventPayload) => UIAnalyticsEvent) | undefined;

	if (fg('jira_timeline_analytics_harmonization')) {
		// eslint-disable-next-line react-hooks/react-compiler, react-hooks/rules-of-hooks
		createAnalyticsEvent = useAnalyticsEvents().createAnalyticsEvent;
	}

	return useCallback(
		(
			updatedIssues: Issue[],
			newParentId: IssueId | null,
			newIssueParent: IssueParent | null,
			requestPromise: Promise<unknown>,
		) => {
			if (updatedIssues.length) {
				const updatedIssue: Issue = updatedIssues[0];
				if (updatedIssue.id) {
					const ariIssueId = JiraIssueAri.create({
						issueId: updatedIssue.id,
						siteId: cloudId,
					}).toString();

					if (createAnalyticsEvent && fg('jira_timeline_analytics_harmonization')) {
						getUpdateAnalyticsFlowHelper().fireAnalyticsEnd(PARENT_TYPE, {
							analytics: createAnalyticsEvent({}),
							attributes: {
								issueId: updatedIssue.id,
								fieldType: FIELD_TYPE_MAP[PARENT_TYPE],
								oldValId: updatedIssue.parentId,
								newValId: newParentId,
							},
						});
					}

					commitLocalUpdate(environment, (store) => {
						updateParentConnections({
							store,
							issueId: ariIssueId,
							oldParentId: updatedIssue.parentId,
							newParentId,
							index: 0,
							projectKey,
						});
					});
					requestPromise.catch((error: Error) => {
						// Rollback
						commitLocalUpdate(environment, (store) => {
							updateParentConnections({
								store,
								issueId: ariIssueId,
								oldParentId: newParentId,
								newParentId: updatedIssue.parentId,
								index: 0,
								projectKey,
							});
						});

						fireErrorAnalytics({
							meta: {
								id: 'issueTableReparentMutationFromModal',
								packageName: PACKAGE_NAME,
								teamName: TEAM_NAME,
							},
							error,
							sendToPrivacyUnsafeSplunk: true,
						});
						showFlag(UPDATE_ITEM_PARENT_FAIL_GENERIC_FLAG);
					});
				}
			}
		},
		[cloudId, createAnalyticsEvent, environment, projectKey, showFlag, updateParentConnections],
	);
};
